import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { WORKFLOWS } from '../lib/mock-data';
import { Play, Settings2, Clock, FileText, FolderTree, Stethoscope, Tags, CheckCircle2, AlertCircle, XCircle, Activity, ChevronRight, X, ShieldAlert, FileSearch, History, User, Briefcase } from 'lucide-react';
interface WorkflowsProps {
  onNavigate: (page: string) => void;
}
// Map the mock data to the detailed templates needed for the UI
const WORKFLOW_TEMPLATES = WORKFLOWS.map(wf => {
  let icon = Settings2;
  let objective = '';
  let inputs: string[] = [];
  let outputs: string[] = [];
  let steps: string[] = [];
  let exceptions: string[] = [];
  let avgTime = '5m 00s';
  let successRate = 95.0;
  if (wf.name.includes('Bates')) {
    icon = Tags;
    objective = 'Standardize document production formatting and ensure sequential numbering without gaps.';
    inputs = ['Raw PDF/TIFF files', 'Starting Bates prefix/number', 'Confidentiality designations'];
    outputs = ['Stamped PDFs', 'Load file (.dat/.opt)', 'Production log'];
    steps = ['Extract text/metadata', 'Identify existing stamps', 'Apply new Bates stamps', 'Generate load file'];
    exceptions = ['Encrypted PDFs', 'Corrupted files', 'Existing conflicting stamps'];
    avgTime = '4m 12s';
    successRate = 99.8;
  } else if (wf.name.includes('Chronology')) {
    icon = Stethoscope;
    objective = 'Synthesize voluminous medical records into a chronological timeline of care and injuries.';
    inputs = ['Medical records (PDF)', 'Provider list (optional)', 'Date range'];
    outputs = ['Chronology table (Word/Excel)', 'Source-linked citations', 'Missing records report'];
    steps = ['OCR & text extraction', 'Entity recognition (Dates, Providers, Treatments)', 'Timeline assembly', 'Confidence scoring'];
    exceptions = ['Handwritten notes (<60% confidence)', 'Conflicting dates', 'Unrecognized provider names'];
    avgTime = '12m 45s';
    successRate = 92.4;
  } else if (wf.name.includes('Classify')) {
    icon = FileSearch;
    objective = 'Rapidly triage incoming document dumps to identify key evidence and filter out noise.';
    inputs = ['Opposing production volume', 'Case pleadings (for context)'];
    outputs = ['Categorized document folders', 'Relevance index', 'Privilege log draft'];
    steps = ['Text extraction', 'Semantic classification', 'Relevance scoring against case theory', 'Folder routing'];
    exceptions = ['Non-searchable images', 'Foreign language documents', 'Password protected files'];
    avgTime = '8m 30s';
    successRate = 95.1;
  } else if (wf.name.includes('Intake')) {
    icon = FolderTree;
    objective = 'Ensure consistent file organization and system setup for every new matter.';
    inputs = ['Client Name', 'Matter Type', 'Lead Attorney'];
    outputs = ['Local directory structure', 'Matter management record', 'Populated intake templates'];
    steps = ['Create directories', 'Copy templates', 'Update database', 'Send notification'];
    exceptions = ['Invalid characters in name', 'Database connection timeout'];
    avgTime = '0m 45s';
    successRate = 100;
  } else if (wf.name.includes('Deposition')) {
    icon = FileText;
    objective = 'Extract key admissions, contradictions, and factual statements from deposition transcripts.';
    inputs = ['Deposition Transcript (.txt/.pdf)', 'Case Theory Memo'];
    outputs = ['Page/Line Summary', 'Contradiction Report', 'Key Admissions List'];
    steps = ['Parse transcript format', 'Identify Q&A pairs', 'Extract factual claims', 'Cross-reference with corpus'];
    exceptions = ['Poor formatting/OCR', 'Unidentified speakers', 'Off-the-record discussions'];
    avgTime = '6m 15s';
    successRate = 88.5;
  }
  return {
    ...wf,
    icon,
    objective,
    inputs,
    outputs,
    steps,
    exceptions,
    avgTime,
    successRate
  };
});
const ACTIVE_JOBS = [{
  id: 'job-1042',
  workflow: 'Generate Medical Chronology',
  matter: "Nguyen v. St. Luke's Medical Center",
  status: 'Running',
  progress: 65,
  duration: '8m 12s',
  reviewer: 'Sarah Jenkins'
}, {
  id: 'job-1043',
  workflow: 'Classify Production Set',
  matter: 'Castillo v. Westfield Properties',
  status: 'Running',
  progress: 32,
  duration: '2m 45s',
  reviewer: 'David Chen'
}, {
  id: 'job-1044',
  workflow: 'Bates Label Production',
  matter: 'Garcia v. Metro Transit Authority',
  status: 'Queued',
  progress: 0,
  duration: '-',
  reviewer: 'Michael Ross'
}];
const RECENT_EXCEPTIONS = [{
  id: 'exc-892',
  workflow: 'Extract Deposition Summary',
  matter: "Nguyen v. St. Luke's Medical Center",
  issue: 'Transcript formatting not recognized (missing Q/A markers)',
  time: '2 hours ago',
  status: 'Needs Review'
}, {
  id: 'exc-891',
  workflow: 'Generate Medical Chronology',
  matter: 'Ramirez v. Lone Star Freight',
  issue: 'Conflicting dates detected for surgery event',
  time: '4 days ago',
  status: 'Flagged'
}];
const COMPLETED_JOBS = [{
  id: 'job-1041',
  workflow: 'Intake Folder Creation',
  matter: 'Thompson v. ABC Trucking Inc.',
  status: 'Success',
  duration: '42s',
  time: '3 hours ago',
  reviewer: 'Auto'
}, {
  id: 'job-1040',
  workflow: 'Bates Label Production',
  matter: 'Ramirez v. Lone Star Freight',
  status: 'Success',
  duration: '3m 15s',
  time: '5 hours ago',
  reviewer: 'Spot Checked'
}, {
  id: 'job-1039',
  workflow: 'Classify Production Set',
  matter: 'Garcia v. Metro Transit Authority',
  status: 'Completed w/ Flags',
  duration: '14m 20s',
  time: 'Yesterday',
  reviewer: 'Pending Review'
}];
export function Workflows({
  onNavigate
}: WorkflowsProps) {
  const [selectedWorkflow, setSelectedWorkflow] = useState<(typeof WORKFLOW_TEMPLATES)[0] | null>(null);
  return <div className="space-y-6 pb-12 relative" data-id="element-2325">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 bg-slate-900 text-white p-6 rounded-xl shadow-md" data-id="element-2326">
        <div className="max-w-2xl" data-id="element-2327">
          <h1 className="text-2xl font-bold tracking-tight flex items-center" data-id="element-2328">
            <Activity className="w-6 h-6 mr-3 text-blue-400" data-id="element-2329" />
            Workflow Automation Center
          </h1>
          <p className="text-slate-300 mt-2 text-sm leading-relaxed" data-id="element-2330">
            Standardize, automate, and audit repetitive legal operations.
            <span className="font-semibold text-amber-400 ml-1" data-id="element-2331">
              All AI-generated outputs require attorney review before use.
            </span>
          </p>
        </div>
        <div className="flex gap-3 shrink-0" data-id="element-2332">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center min-w-[100px]" data-id="element-2333">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1" data-id="element-2334">
              Active Jobs
            </p>
            <p className="text-2xl font-bold text-blue-400" data-id="element-2335">3</p>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center min-w-[100px]" data-id="element-2336">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1" data-id="element-2337">
              Completed
            </p>
            <p className="text-2xl font-bold text-emerald-400" data-id="element-2338">8</p>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center min-w-[100px]" data-id="element-2339">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1" data-id="element-2340">
              Success Rate
            </p>
            <p className="text-2xl font-bold text-white" data-id="element-2341">96.4%</p>
          </div>
        </div>
      </div>

      {/* Workflow Templates Grid */}
      <div data-id="element-2342">
        <h2 className="text-lg font-semibold mb-4 flex items-center" data-id="element-2343">
          <Settings2 className="w-5 h-5 mr-2 text-slate-500" data-id="element-2344" />
          Available Workflows
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2345">
          {WORKFLOW_TEMPLATES.map(workflow => {
          const Icon = workflow.icon;
          return <Card key={workflow.id} className="flex flex-col hover:border-blue-300 transition-colors shadow-sm" data-id="element-2346">

                <CardHeader className="pb-3" data-id="element-2347">
                  <div className="flex justify-between items-start mb-3" data-id="element-2348">
                    <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg" data-id="element-2349">
                      <Icon className="w-5 h-5" data-id="element-2350" />
                    </div>
                    <Badge variant={workflow.status === 'Active' ? 'success' : workflow.status === 'Queued' ? 'warning' : 'secondary'} data-id="element-2351">

                      {workflow.status}
                    </Badge>
                  </div>
                  <CardTitle className="text-base" data-id="element-2352">{workflow.name}</CardTitle>
                  <CardDescription className="h-10 text-xs mt-1 line-clamp-2" data-id="element-2353">
                    {workflow.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="py-3 text-xs space-y-3 border-y bg-slate-50/50" data-id="element-2354">
                  <div className="flex items-center justify-between text-slate-600" data-id="element-2355">
                    <div className="flex items-center" data-id="element-2356">
                      <Briefcase className="w-3.5 h-3.5 mr-1.5 text-slate-400" data-id="element-2357" />
                      <span className="truncate max-w-[150px]" data-id="element-2358">
                        {workflow.matter}
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-2359">
                      <User className="w-3.5 h-3.5 mr-1.5 text-slate-400" data-id="element-2360" />
                      <span data-id="element-2361">{workflow.assignedTo}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-slate-200" data-id="element-2362">
                    <span className="text-muted-foreground" data-id="element-2363">Review Req:</span>
                    <Badge variant="outline" className="text-[10px] bg-white text-amber-700 border-amber-200" data-id="element-2364">

                      {workflow.reviewCheckpoint}
                    </Badge>
                  </div>
                </CardContent>
                <CardFooter className="pt-4 mt-auto flex justify-between" data-id="element-2365">
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setSelectedWorkflow(workflow)} data-id="element-2366">

                    View Details
                  </Button>
                  <Button size="sm" className="h-8 text-xs" onClick={() => workflow.name.includes('Chronology') ? onNavigate('chronology') : null} data-id="element-2367">

                    <Play className="w-3 h-3 mr-1.5" data-id="element-2368" /> Run Job
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>

      {/* Operations Console Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8" data-id="element-2369">
        {/* Left Column: Active & Exceptions */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2370">
          {/* Active Jobs */}
          <Card className="shadow-sm border-blue-100" data-id="element-2371">
            <CardHeader className="py-4 border-b bg-blue-50/30" data-id="element-2372">
              <CardTitle className="text-base flex items-center" data-id="element-2373">
                <Activity className="w-4 h-4 mr-2 text-blue-600" data-id="element-2374" />
                In-Progress Jobs
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0" data-id="element-2375">
              <table className="w-full text-sm text-left" data-id="element-2376">
                <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b" data-id="element-2377">
                  <tr data-id="element-2378">
                    <th className="px-4 py-3 font-medium" data-id="element-2379">Job ID</th>
                    <th className="px-4 py-3 font-medium" data-id="element-2380">Workflow / Matter</th>
                    <th className="px-4 py-3 font-medium w-1/3" data-id="element-2381">Progress</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-2382">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-id="element-2383">
                  {ACTIVE_JOBS.map(job => <tr key={job.id} className="hover:bg-muted/30" data-id="element-2384">
                      <td className="px-4 py-3 text-xs font-mono text-muted-foreground" data-id="element-2385">
                        {job.id}
                      </td>
                      <td className="px-4 py-3" data-id="element-2386">
                        <p className="font-medium text-slate-900" data-id="element-2387">
                          {job.workflow}
                        </p>
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]" data-id="element-2388">
                          {job.matter}
                        </p>
                      </td>
                      <td className="px-4 py-3" data-id="element-2389">
                        {job.status === 'Running' ? <div className="space-y-1.5" data-id="element-2390">
                            <div className="flex justify-between text-xs" data-id="element-2391">
                              <span className="text-blue-600 font-medium" data-id="element-2392">
                                {job.progress}%
                              </span>
                              <span className="text-muted-foreground" data-id="element-2393">
                                {job.duration}
                              </span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-2394">
                              <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{
                          width: `${job.progress}%`
                        }} data-id="element-2395">
                          </div>
                            </div>
                          </div> : <Badge variant="secondary" className="text-[10px] font-normal" data-id="element-2396">

                            Queued
                          </Badge>}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-2397">
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-2398">

                          Cancel
                        </Button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Exceptions Panel */}
          <Card className="shadow-sm border-red-200" data-id="element-2399">
            <CardHeader className="py-4 border-b bg-red-50/50" data-id="element-2400">
              <CardTitle className="text-base flex items-center text-red-900" data-id="element-2401">
                <ShieldAlert className="w-4 h-4 mr-2 text-red-600" data-id="element-2402" />
                Exceptions & Failed Jobs
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0" data-id="element-2403">
              <div className="divide-y divide-red-100" data-id="element-2404">
                {RECENT_EXCEPTIONS.map(exc => <div key={exc.id} className="p-4 hover:bg-red-50/30 transition-colors flex items-start justify-between gap-4" data-id="element-2405">

                    <div className="flex items-start space-x-3" data-id="element-2406">
                      <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" data-id="element-2407" />
                      <div data-id="element-2408">
                        <div className="flex items-center space-x-2 mb-1" data-id="element-2409">
                          <p className="font-medium text-slate-900" data-id="element-2410">
                            {exc.workflow}
                          </p>
                          <Badge variant={exc.status === 'Flagged' ? 'warning' : 'destructive'} className="text-[10px] h-5" data-id="element-2411">

                            {exc.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-red-800 mb-1" data-id="element-2412">{exc.issue}</p>
                        <p className="text-xs text-muted-foreground" data-id="element-2413">
                          Matter: {exc.matter} • {exc.time}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="shrink-0 h-8 text-xs" data-id="element-2414">

                      Resolve
                    </Button>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: History */}
        <div className="space-y-6" data-id="element-2415">
          <Card className="shadow-sm h-full flex flex-col" data-id="element-2416">
            <CardHeader className="py-4 border-b" data-id="element-2417">
              <CardTitle className="text-base flex items-center" data-id="element-2418">
                <History className="w-4 h-4 mr-2 text-slate-500" data-id="element-2419" />
                Recent History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto" data-id="element-2420">
              <div className="divide-y" data-id="element-2421">
                {COMPLETED_JOBS.map(job => <div key={job.id} className="p-4 hover:bg-muted/30 transition-colors" data-id="element-2422">

                    <div className="flex items-start justify-between mb-1" data-id="element-2423">
                      <p className="text-sm font-medium truncate pr-2" data-id="element-2424">
                        {job.workflow}
                      </p>
                      <Badge variant={job.status === 'Success' ? 'success' : 'warning'} className="text-[10px] shrink-0" data-id="element-2425">

                        {job.status === 'Success' ? <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-2426" /> : <AlertCircle className="w-3 h-3 mr-1" data-id="element-2427" />}
                        {job.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mb-2" data-id="element-2428">
                      {job.matter}
                    </p>
                    <div className="flex items-center justify-between text-xs" data-id="element-2429">
                      <span className="text-slate-500 flex items-center" data-id="element-2430">
                        <Clock className="w-3 h-3 mr-1" data-id="element-2431" /> {job.duration}
                      </span>
                      <span className="text-slate-500" data-id="element-2432">
                        Rev:{' '}
                        <span className="font-medium text-slate-700" data-id="element-2433">
                          {job.reviewer}
                        </span>
                      </span>
                    </div>
                  </div>)}
              </div>
            </CardContent>
            <CardFooter className="p-3 border-t bg-muted/20" data-id="element-2434">
              <Button variant="ghost" size="sm" className="w-full text-xs" data-id="element-2435">
                View Full Audit Log
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Slide-out Detail Panel (Overlay) */}
      {selectedWorkflow && <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm transition-opacity" data-id="element-2436">
          <div className="w-full max-w-md md:max-w-xl lg:max-w-2xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300" data-id="element-2437">
            <div className="flex items-center justify-between p-6 border-b bg-slate-50" data-id="element-2438">
              <div className="flex items-center space-x-3" data-id="element-2439">
                <div className="p-2 bg-blue-100 text-blue-700 rounded-lg" data-id="element-2440">
                  <selectedWorkflow.icon className="w-6 h-6" data-id="element-2441" />
                </div>
                <div data-id="element-2442">
                  <h2 className="text-xl font-bold" data-id="element-2443">{selectedWorkflow.name}</h2>
                  <div className="flex items-center space-x-2 mt-1" data-id="element-2444">
                    <Badge variant={selectedWorkflow.status === 'Active' ? 'success' : 'secondary'} data-id="element-2445">

                      {selectedWorkflow.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono" data-id="element-2446">
                      ID: {selectedWorkflow.id}
                    </span>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedWorkflow(null)} className="h-8 w-8 p-0 rounded-full" data-id="element-2447">

                <X className="w-5 h-5 text-slate-500" data-id="element-2448" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-2449">
              <section data-id="element-2450">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center" data-id="element-2451">
                  <FileText className="w-4 h-4 mr-2 text-slate-400" data-id="element-2452" /> Objective
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 p-4 rounded-lg border" data-id="element-2453">
                  {selectedWorkflow.objective}
                </p>
              </section>

              <div className="grid grid-cols-2 gap-6" data-id="element-2454">
                <section data-id="element-2455">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-2456">
                    Required Inputs
                  </h3>
                  <ul className="space-y-2" data-id="element-2457">
                    {selectedWorkflow.inputs.map((input, i) => <li key={i} className="flex items-start text-sm text-slate-700" data-id="element-2458">

                        <ChevronRight className="w-4 h-4 text-blue-500 mr-1 shrink-0 mt-0.5" data-id="element-2459" />
                        {input}
                      </li>)}
                  </ul>
                </section>
                <section data-id="element-2460">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-2461">
                    Output Artifacts
                  </h3>
                  <ul className="space-y-2" data-id="element-2462">
                    {selectedWorkflow.outputs.map((output, i) => <li key={i} className="flex items-start text-sm text-slate-700" data-id="element-2463">

                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-1 shrink-0 mt-0.5" data-id="element-2464" />
                        {output}
                      </li>)}
                  </ul>
                </section>
              </div>

              <section data-id="element-2465">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center" data-id="element-2466">
                  <Settings2 className="w-4 h-4 mr-2 text-slate-400" data-id="element-2467" />{' '}
                  Processing Steps
                </h3>
                <div className="space-y-0 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 before:to-transparent" data-id="element-2468">
                  {selectedWorkflow.steps.map((step, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active mb-4" data-id="element-2469">

                      <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-blue-100 text-blue-600 text-xs font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2470">
                        {i + 1}
                      </div>
                      <div className="w-[calc(100%-3rem)] md:w-[calc(50%-2rem)] p-3 rounded border border-slate-200 bg-white shadow-sm" data-id="element-2471">
                        <div className="text-sm font-medium text-slate-800" data-id="element-2472">
                          {step}
                        </div>
                      </div>
                    </div>)}
                </div>
              </section>

              <section className="bg-amber-50 rounded-lg border border-amber-200 p-4" data-id="element-2473">
                <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider mb-3 flex items-center" data-id="element-2474">
                  <ShieldAlert className="w-4 h-4 mr-2 text-amber-600" data-id="element-2475" /> Review
                  & Exceptions
                </h3>
                <div className="mb-4" data-id="element-2476">
                  <p className="text-xs font-semibold text-amber-800 mb-1" data-id="element-2477">
                    Approval Requirement:
                  </p>
                  <Badge variant="warning" className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-id="element-2478">

                    {selectedWorkflow.reviewCheckpoint}
                  </Badge>
                </div>
                <div data-id="element-2479">
                  <p className="text-xs font-semibold text-amber-800 mb-2" data-id="element-2480">
                    Known Exception Conditions:
                  </p>
                  <ul className="space-y-1.5" data-id="element-2481">
                    {selectedWorkflow.exceptions.map((exc, i) => <li key={i} className="flex items-start text-sm text-amber-900" data-id="element-2482">

                        <AlertCircle className="w-4 h-4 text-amber-500 mr-2 shrink-0 mt-0.5" data-id="element-2483" />
                        {exc}
                      </li>)}
                  </ul>
                </div>
              </section>
            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-between items-center shrink-0" data-id="element-2484">
              <Button variant="outline" onClick={() => setSelectedWorkflow(null)} data-id="element-2485">

                Close
              </Button>
              <Button onClick={() => {
            setSelectedWorkflow(null);
            if (selectedWorkflow.name.includes('Chronology')) onNavigate('chronology');
          }} data-id="element-2486">

                <Play className="w-4 h-4 mr-2" data-id="element-2487" /> Run Workflow Now
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}